import {
    CIRCLE_DIMENSIONS,
    Colors,
    FOCUS_RING,
    Icon,
    Modal,
    useDialogFocus,
    type IconName,
} from '@amelio/ui-library';
import {
    forwardRef,
    useId,
    useImperativeHandle,
    useRef,
    useState,
    type ReactNode,
    type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AiPromptChoiceStep } from './components/AiPromptChoiceStep';
import { AiPromptIntegrationStep } from './components/AiPromptIntegrationStep';
import { AiPromptModalHeader } from './components/AiPromptModalHeader';
import { AiPromptModalImage } from './components/AiPromptModalImage';
import { AiPromptQuestionStep } from './components/AiPromptQuestionStep';
import { AiPromptReplaceConfirm } from './components/AiPromptReplaceConfirm';

import type { TranslationKey } from '@/i18n-key';

export type AiPromptIntegrationMode = 'add' | 'replace';

/** One free-text question step in the modal (the bold header + the input placeholder). */
export interface AiPromptTextStepConfig {
    kind?: 'text';
    /** ui-library icon name shown beside the question header. */
    iconName: IconName;
    questionKey: TranslationKey;
    placeholderKey: TranslationKey;
    rows?: number;
    /** When true, the step can be left blank. */
    optional?: boolean;
}

/** One option of a choice step: the value sent as the step's answer and the label shown for it. */
export interface AiPromptChoiceOption {
    value: string;
    labelKey: TranslationKey;
}

/** One single-choice question step: the answer is always one of the options, so it is never blank. */
export interface AiPromptChoiceStepConfig {
    kind: 'choice';
    /** ui-library icon name shown beside the question header. */
    iconName: IconName;
    questionKey: TranslationKey;
    options: AiPromptChoiceOption[];
    /** The option picked when the modal opens without a prefilled answer for this step. */
    defaultValue: string;
    /** A choice always holds an option, so it is never optional. */
    optional?: never;
}

export type AiPromptStepConfig = AiPromptTextStepConfig | AiPromptChoiceStepConfig;

/** What a step answers before the manager touches it: the default option for a choice, blank for text. */
const initialAnswer = (stepConfig: AiPromptStepConfig, prefill?: string): string =>
    prefill ?? (stepConfig.kind === 'choice' ? stepConfig.defaultValue : '');

export interface AiPromptIntegrationConfig {
    titleKey: TranslationKey;
    add: { titleKey: TranslationKey; descriptionKey: TranslationKey };
    replace: { titleKey: TranslationKey; descriptionKey: TranslationKey };
    /** When set, choosing "replace" shows this confirmation before committing. */
    replaceConfirm?: AiPromptReplaceConfirmConfig;
}

export interface AiPromptReplaceConfirmConfig {
    titleKey: TranslationKey;
    messageKey: TranslationKey;
    questionKey: TranslationKey;
    confirmKey: TranslationKey;
    cancelKey: TranslationKey;
}

export interface AiPromptModalConfig {
    headerTitleKey: TranslationKey;
    imageAltKey: TranslationKey;
    overlayGeneratingKey: TranslationKey;
    overlayMessageKey: TranslationKey;
    /** Label of the generate button on the final question step. */
    generateButtonKey: TranslationKey;
    steps: AiPromptStepConfig[];
    integration: AiPromptIntegrationConfig;
}

/**
 * Thrown by `onGenerate` when the server refuses what a question's answer says: the modal shows `message` and
 * `items` under that question, goes back to it, and does not generate again until its answer is edited.
 */
export class AiPromptStepError extends Error {
    readonly step: number;
    readonly items: string[];

    constructor(step: number, message: string, items: string[] = []) {
        super(message);
        this.name = 'AiPromptStepError';
        this.step = step;
        this.items = items;
    }
}

/**
 * Thrown by `onGenerate` when the server refuses something the modal does not ask for, which is edited elsewhere:
 * the modal shows `message` and `items` above the generate button and keeps it disabled until the modal is opened
 * again, since no answer in the modal can lift it. `stepError` is an answer refused at the same time.
 */
export class AiPromptBlockedError extends Error {
    readonly items: string[];
    readonly stepError: AiPromptStepError | null;

    constructor(message: string, items: string[] = [], stepError: AiPromptStepError | null = null) {
        super(message);
        this.name = 'AiPromptBlockedError';
        this.items = items;
        this.stepError = stepError;
    }
}

/** A refusal shown under one question, with the answer it was given for. */
interface ShownStepError {
    step: number;
    message: string;
    items: string[];
    answer: string;
}

export interface AiPromptModalRef {
    /** Opens on the first question; `initialAnswers` prefills the steps, one answer per step, in order. */
    open: (initialAnswers?: string[]) => void;
    close: () => void;
}

export interface AiPromptModalProps<T> {
    config: AiPromptModalConfig;
    /**
     * Runs the real generation with the collected answers (one per step) and resolves to the generated
     * payload, or null on failure/timeout. The modal advances to the integration step only on a non-null result.
     * It may throw an {@link AiPromptStepError} to send the manager back to the answer the server refused, or an
     * {@link AiPromptBlockedError} when what was refused is not one of the answers.
     */
    onGenerate: (answers: string[]) => Promise<T | null>;
    /** Integrates the generated payload, either appended (add) or overwriting (replace). */
    onIntegrate: (mode: AiPromptIntegrationMode, result: T) => void;
    /** When false, the "Add" option is disabled (there's no existing content to add to). Defaults to true. */
    canAdd?: boolean;
    /**
     * Why the generated payload cannot be added to the current content, or null when it can: "Add" is then
     * disabled and this message is shown under it, so the manager knows to replace instead.
     */
    addBlockedReason?: (result: T) => string | null;
    /** What to show about the generated payload above the Add / Replace choice (e.g. its summary and alerts). */
    renderResultReview?: (result: T) => ReactNode;
    dataTestId?: string;
}

// Steps: 0..N-1 are the question steps, N is the integration step, N+1 is the (optional) replace confirmation.
function AiPromptModalInner<T>(
    {
        config,
        onGenerate,
        onIntegrate,
        canAdd = true,
        addBlockedReason,
        renderResultReview,
        dataTestId = 'ai-prompt-modal',
    }: AiPromptModalProps<T>,
    ref: Ref<AiPromptModalRef>,
) {
    const { t } = useTranslation();
    const titleId = useId();

    const stepCount = config.steps.length;
    const integrationStep = stepCount;
    const confirmStep = stepCount + 1;

    const [isOpen, setIsOpen] = useState(false);
    const [answers, setAnswers] = useState<string[]>(() =>
        config.steps.map((stepConfig) => initialAnswer(stepConfig)),
    );
    const [step, setStep] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<T | null>(null);
    const [stepError, setStepError] = useState<ShownStepError | null>(null);
    const [blocked, setBlocked] = useState<{ message: string; items: string[] } | null>(null);
    // Bumped on every open: a generation still running when the modal was closed must not land in
    // a fresh session.
    const sessionRef = useRef(0);

    const { dialogRef, handleKeyDown } = useDialogFocus(isOpen);

    const close = () => setIsOpen(false);

    useImperativeHandle(ref, () => ({
        open: (initialAnswers) => {
            sessionRef.current += 1;
            setAnswers(
                config.steps.map((stepConfig, index) =>
                    initialAnswer(stepConfig, initialAnswers?.[index]),
                ),
            );
            setGenerated(null);
            setStepError(null);
            setBlocked(null);
            setStep(0);
            setIsGenerating(false);
            setIsOpen(true);
        },
        close,
    }));

    const setAnswer = (index: number, value: string) =>
        setAnswers((current) => current.map((answer, i) => (i === index ? value : answer)));

    const handleQuestionNext = async () => {
        if (isGenerating) return;
        const isFinal = step === stepCount - 1;
        if (!config.steps[step].optional && !answers[step]?.trim()) return;

        if (!isFinal) {
            setStep(step + 1);
            return;
        }

        // Final step: generate for real, then advance to the integration step on success.
        const session = sessionRef.current;
        setIsGenerating(true);
        let result: T | null = null;
        try {
            result = await onGenerate(answers.map((answer) => answer.trim()));
        } catch (error) {
            if (session !== sessionRef.current) return;
            setIsGenerating(false);
            if (error instanceof AiPromptBlockedError) {
                setBlocked({ message: error.message, items: error.items });
            }
            const refusedAnswer = error instanceof AiPromptBlockedError ? error.stepError : error;
            if (refusedAnswer instanceof AiPromptStepError) {
                setStepError({
                    step: refusedAnswer.step,
                    message: refusedAnswer.message,
                    items: refusedAnswer.items,
                    answer: answers[refusedAnswer.step] ?? '',
                });
                setStep(refusedAnswer.step);
            }
            return;
        }
        if (session !== sessionRef.current) return;
        setIsGenerating(false);
        if (result !== null) {
            setGenerated(result);
            setStep(integrationStep);
        }
    };

    const addBlockedMessage = generated === null ? null : (addBlockedReason?.(generated) ?? null);

    const handleIntegrate = (mode: AiPromptIntegrationMode) => {
        if (generated === null) return;
        if (mode === 'add' && (!canAdd || addBlockedMessage)) return;
        if (mode === 'replace' && config.integration.replaceConfirm) {
            setStep(confirmStep);
            return;
        }
        onIntegrate(mode, generated);
        close();
    };

    // Shown only while the refused answer is unchanged: an edit is the manager's answer to the refusal.
    const shownError =
        stepError && stepError.step === step && answers[step] === stepError.answer
            ? stepError
            : null;

    const currentStep: AiPromptStepConfig | undefined =
        step < stepCount ? config.steps[step] : undefined;

    const confirmReplace = () => {
        if (generated === null) return;
        onIntegrate('replace', generated);
        close();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            // A generation cannot be cancelled: Escape and the backdrop wait for it to end.
            preventDefault={isGenerating}
            className={'max-w-8xl w-[1152px]'}
            padding={'none'}
            radius={'2xl'}
            zIndex={60}
            dataTestId={`${dataTestId}-backdrop`}
        >
            <div
                ref={dialogRef}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={titleId}
                onKeyDown={handleKeyDown}
                data-testid={dataTestId}
                className={'relative flex flex-col md:flex-row'}
            >
                <div className={'flex-1 p-6 md:p-10 lg:p-13'}>
                    <AiPromptModalHeader
                        titleKey={config.headerTitleKey}
                        titleId={titleId}
                    />

                    {currentStep?.kind === 'choice' && (
                        <AiPromptChoiceStep
                            iconName={currentStep.iconName}
                            questionKey={currentStep.questionKey}
                            options={currentStep.options}
                            value={answers[step] ?? currentStep.defaultValue}
                            onChange={(value) => setAnswer(step, value)}
                            onNext={handleQuestionNext}
                            onBack={step > 0 ? () => setStep(step - 1) : undefined}
                            isFinal={step === stepCount - 1}
                            generateButtonKey={config.generateButtonKey}
                            isGenerating={isGenerating}
                            error={shownError ?? undefined}
                            blocked={step === stepCount - 1 ? (blocked ?? undefined) : undefined}
                            stepNumber={step + 1}
                            dataTestId={dataTestId}
                        />
                    )}

                    {currentStep && currentStep.kind !== 'choice' && (
                        <AiPromptQuestionStep
                            iconName={currentStep.iconName}
                            questionKey={currentStep.questionKey}
                            placeholderKey={currentStep.placeholderKey}
                            rows={currentStep.rows}
                            optional={currentStep.optional}
                            value={answers[step] ?? ''}
                            onChange={(value) => setAnswer(step, value)}
                            onNext={handleQuestionNext}
                            onBack={step > 0 ? () => setStep(step - 1) : undefined}
                            isFinal={step === stepCount - 1}
                            generateButtonKey={config.generateButtonKey}
                            isGenerating={isGenerating}
                            error={shownError ?? undefined}
                            blocked={step === stepCount - 1 ? (blocked ?? undefined) : undefined}
                            stepNumber={step + 1}
                            dataTestId={dataTestId}
                        />
                    )}

                    {step === integrationStep && (
                        <AiPromptIntegrationStep
                            config={config.integration}
                            onIntegrate={handleIntegrate}
                            canAdd={canAdd && !addBlockedMessage}
                            addBlockedMessage={addBlockedMessage ?? undefined}
                            review={
                                generated === null ? undefined : renderResultReview?.(generated)
                            }
                            dataTestId={dataTestId}
                        />
                    )}

                    {step === confirmStep && config.integration.replaceConfirm && (
                        <AiPromptReplaceConfirm
                            config={config.integration.replaceConfirm}
                            onConfirm={confirmReplace}
                            onCancel={() => setStep(integrationStep)}
                            dataTestId={dataTestId}
                        />
                    )}
                </div>

                <AiPromptModalImage
                    isGenerating={isGenerating}
                    imageAltKey={config.imageAltKey}
                    overlayGeneratingKey={config.overlayGeneratingKey}
                    overlayMessageKey={config.overlayMessageKey}
                />

                <button
                    type={'button'}
                    onClick={close}
                    disabled={isGenerating}
                    aria-label={t('common.close')}
                    className={`absolute top-4 right-4 z-20 flex ${CIRCLE_DIMENSIONS.lg} hover:bg-base-50 items-center justify-center rounded-full bg-white shadow-md disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`}
                >
                    <Icon
                        name={'essential-close'}
                        size={'md'}
                        color={Colors.BASE_400}
                    />
                </button>
            </div>
        </Modal>
    );
}

// forwardRef loses generics; re-assert the generic signature so callers keep `T` inference.
export const AiPromptModal = forwardRef(AiPromptModalInner) as <T>(
    props: AiPromptModalProps<T> & { ref?: Ref<AiPromptModalRef> },
) => ReturnType<typeof AiPromptModalInner>;
